import { Badge, Column } from '@odin-labs/components';
import {
  SiteReaderOwnership,
  type SiteControllerReaderFieldsFragment,
} from 'apollo/generated/client-operations';
import React from 'react';
import {
  readerLabel,
  ReaderKindBadge,
  ReaderHealth,
} from 'siteLayout/readerPresentation';
import { getFriendlyFormattedDateTime } from 'utils/dates';

export type SiteControllerReaderRow = SiteControllerReaderFieldsFragment;
export type SiteControllerReaderColumn = Column<SiteControllerReaderRow>;

export const ownershipLabels: Record<SiteReaderOwnership, string> = {
  [SiteReaderOwnership.Assigned]: 'This controller',
  [SiteReaderOwnership.Unresolved]: 'Unresolved',
  [SiteReaderOwnership.NotThisController]: 'Another controller',
};

/** Where the reader is placed: the published layout first, then the draft when it differs. */
export const placementText = (
  placement: SiteControllerReaderRow['placement'],
): string => {
  const published = placement.published?.entryName;
  const draft = placement.draft?.entryName;
  if (published && draft && published !== draft)
    return `${published} (draft: ${draft})`;
  if (published) return published;
  if (draft) return `${draft} (draft)`;
  return 'Not placed';
};

enum ReaderColumnId {
  Name = 'name',
  Port = 'port',
  Health = 'health',
  Placement = 'placement',
  Ownership = 'ownership',
  Connection = 'connection',
  Registered = 'registered',
}

export const getReaderColumns = (
  controllerName: string,
): SiteControllerReaderColumn[] => [
  {
    id: ReaderColumnId.Name,
    Header: 'Reader',
    accessor: 'name',
    Cell: ({
      row,
    }: {
      row: { original: SiteControllerReaderRow };
    }): React.ReactElement => (
      <span className="odin-flex odin-flex-col odin-items-start odin-gap-1">
        <span>
          {readerLabel(row.original, controllerName, row.original.port?.label)}
        </span>
        <ReaderKindBadge kind={row.original.kind} />
        {row.original.serialNumber && (
          <span className="odin-text-xs odin-text-gray-500">
            {row.original.serialNumber}
          </span>
        )}
      </span>
    ),
  },
  {
    id: ReaderColumnId.Port,
    Header: 'Port',
    accessor: (row: SiteControllerReaderRow): string =>
      row.port?.label ?? 'Unplugged',
  },
  {
    id: ReaderColumnId.Health,
    Header: 'Health',
    Cell: ({
      row,
    }: {
      row: { original: SiteControllerReaderRow };
    }): React.ReactElement => <ReaderHealth reader={row.original} />,
  },
  {
    id: ReaderColumnId.Placement,
    Header: 'Placement',
    accessor: (row: SiteControllerReaderRow): string =>
      placementText(row.placement),
  },
  {
    id: ReaderColumnId.Ownership,
    Header: 'Belongs to',
    accessor: (row: SiteControllerReaderRow): string =>
      ownershipLabels[row.ownership],
    Cell: ({
      row,
    }: {
      row: { original: SiteControllerReaderRow };
    }): React.ReactElement =>
      row.original.ownership === SiteReaderOwnership.Assigned ? (
        <span>{ownershipLabels[row.original.ownership]}</span>
      ) : (
        <Badge text={ownershipLabels[row.original.ownership]} color="yellow" />
      ),
  },
  {
    id: ReaderColumnId.Connection,
    Header: 'Avigilon',
    accessor: (row: SiteControllerReaderRow): string => {
      if (!row.connection) return 'Not connected';
      return row.connection.externalName || row.connection.externalId;
    },
  },
  {
    id: ReaderColumnId.Registered,
    Header: 'Registered on this site',
    accessor: (row: SiteControllerReaderRow): string =>
      `${getFriendlyFormattedDateTime(row.registeredAt)}${
        row.registeredBy ? ` by ${row.registeredBy}` : ''
      }`,
  },
];
